import { inspectJsonSafety } from "../safety/jsonSafety";
import { blockedBy, failed, type ToolResult } from "../manifest/status";
import type { JsonSchema } from "../manifest/schema";
import { validateArguments } from "../manifest/validate";
import type { JsonObject, JsonValue } from "../wire/json";
import type { WireTool } from "../wire/protocol";

/**
 * The in-frame dispatcher: the last thing that runs before a result leaves the
 * app frame, and the first thing that runs after a call arrives in it.
 *
 * Four jobs, in this order, and the order is the design:
 *
 * 1. **Is this tool ours?** An unknown name is `unknown_tool`, never a silent
 *    no-op.
 * 2. **Do the arguments match the declared schema?** The edge checks this too;
 *    this is not redundancy for its own sake. At stage 1 there was no edge, and
 *    the bridge is the last line where a bad argument can be named rather than
 *    producing a confusing host error.
 * 3. **Is another call already running in this frame?** A second concurrent
 *    call is REFUSED, not queued (the refuse-don't-queue rule, `docs/error-taxonomy.md` § 2) — and refusal composes with
 *    the never-retry rule, because a call refused before it started is
 *    confirmably not started.
 * 4. **Is the result JSON-safe?** Checked HERE, in the app frame, which is the
 *    earliest point at which a violation can be attributed to the tool that
 *    produced it (`docs/tool-surface.md`). A `Map` that reaches the socket is already an empty
 *    object and there is nothing left to name.
 */

export type ToolHandler = (
  args: JsonObject,
) => Promise<ToolResult<JsonValue>> | ToolResult<JsonValue>;

export class BridgeDispatcher {
  private inFlight: string | null = null;

  constructor(
    private readonly tools: readonly WireTool[],
    private readonly handlers: Readonly<Record<string, ToolHandler>>,
  ) {}

  toolList(): readonly WireTool[] {
    return this.tools;
  }

  async handle(tool: string, args: JsonObject): Promise<ToolResult<JsonValue>> {
    const handler = this.handlers[tool];
    if (handler === undefined) {
      return failed(
        "unknown_tool",
        `This app frame does not implement "${tool}". It implements: ` +
          `${Object.keys(this.handlers).join(", ")}.`,
      );
    }

    // ADVERTISED and IMPLEMENTED are different sets, on purpose. An app
    // advertises its own `<app>__*` tools; it also implements the fixed host
    // tools (`giraffe_state`, and the SDK tools in the control frame), which are
    // declared in `manifest/` and contributed to the live list by the RELAY,
    // not by the app. Those arrive already validated against the manifest
    // schema, so there is nothing here to re-check — and inventing a second
    // copy of their schema in every app is how two versions of one contract
    // start to drift.
    const declared = this.tools.find((entry) => entry.name === tool);
    if (declared !== undefined) {
      const validation = validateArguments(declared.inputSchema as unknown as JsonSchema, args);
      if (!validation.ok) {
        return failed("invalid_arguments", `${validation.path}: ${validation.message}`);
      }
    }

    if (this.inFlight !== null) return blockedBy(this.inFlight);

    this.inFlight = tool;
    try {
      const result = await handler(args);
      const safety = inspectJsonSafety(result, "result");
      if (!safety.ok) {
        return failed(
          "not_serializable",
          `${safety.violation.message} The tool ran; its result could not be sent, so whether ` +
            `it took effect in the project is unknown from this answer alone.`,
        );
      }
      return result;
    } catch (error) {
      return failed("host_rejected", describeHostRejection(tool, args, error));
    } finally {
      this.inFlight = null;
    }
  }
}

/**
 * `docs/error-taxonomy.md`: `host_rejected` must ADMIT WHAT IT CANNOT TELL YOU.
 *
 * The SDK's `InvocationError` keeps `procedureName`, `cause` and
 * `previousRejectReasons` private, so this message carries what we DO have —
 * the tool, the arguments we sent, and `error.message` — and then says the SDK
 * seals the rest. Without that sentence the next reader spends an afternoon
 * looking for a swallowing bug in our code.
 */
function describeHostRejection(tool: string, args: JsonObject, error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  const argumentNames = Object.keys(args);
  return (
    `The Giraffe host rejected "${tool}" (arguments: ` +
    `${argumentNames.length === 0 ? "none" : argumentNames.join(", ")}). It said: ${message}. ` +
    `The SDK's InvocationError seals procedureName, cause and previousRejectReasons, so no ` +
    `further detail is available from this side — this is not a swallowed error in the bridge.`
  );
}
